import {
  Button,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Table,
  Tag,
  Spin,
  Input,
  Avatar,
  Typography,
} from "antd";
import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { colors } from "../../../../../../constants/colors";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import { CheckCircleFilled, CloseCircleFilled, UserAddOutlined } from "@ant-design/icons";
import { SearchOutlined } from "@ant-design/icons";
import { getCustomersAction } from "../../../../../../redux/services/customers/customersServices";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { routes, userRoles } from "../../../../../../constants";
import AcceptRejectStatusDialog from "./AcceptRejectStatusDialog";
import Actions from "../../../../../../redux/actions";
import useDebounce from "../../../../../../hooks/useDebounce";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";

const LoginAuth = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { customers } = useSelector((state) => state.customers);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: customers?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* debounced loading for search */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    1000
  );
  /* debounced loading for search */

  /* dialog dependencies */
  const [openAcceptRejectStatusDialog, setOpenAcceptRejectStatusDialog] =
    useState(false);
  const [status, setStatus] = useState("");
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: searchValue,
      role: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR,
      is_doctor_verified: false,
    };
    dispatch(getCustomersAction(dataToBeSent)).then(() => {
      setLoading(false);
    });
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [debouncedSearchValue, pagination]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("activeSubs.organization"),
        render: (_,record) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                size="large"
                src={record?.organization_id?.logo_image}
                key={record?.organization_id?.logo_image}
                icon={<UserAddOutlined />}
              />
               {
                record?.organization_id?.name
            ? `${record?.organization_id?.name}`
            : "-"
               }
            </Flex>
          );
        },
      },
      {
        title: t("activeSubs.doctor"),
        render: (_, record) =>
          record?.first_name || record?.first_name
            ? `${record?.first_name} ${record?.last_name}`
            : "-",
      },
      {
        title: t("activeSubs.loginType"),
        dataIndex: "role",
        key: "role",
      },
      
      {
        title: t("Modules.AutoCredit.status"),
        key: "is_doctor_verified",
        dataIndex: "is_doctor_verified",
        align: "center",
        render: (_, { is_doctor_verified }) => (
          <Tag
            color={is_doctor_verified ? "green" : "red"}
            key={is_doctor_verified}
          >
            {is_doctor_verified ? t("CommonUtils.verified") : t("CommonUtils.notVerified")}
          </Tag>
        ),
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {!record?.is_doctor_verified ? (
                <Flex gap="small" justify="center">
                  <Tag
                    bordered={false}
                    style={{
                      background: colors.lightTeal,
                      color: colors.darkTeal,
                      cursor: "pointer",
                    }}
                    icon={<CheckCircleFilled />}
                    onClick={(e) =>
                      handleConfirmStatus(e, record, "isAccepted")
                    }
                  >
                    {t("CommonUtils.accept")}
                  </Tag>

                  <Tag
                    bordered={false}
                    style={{
                      background: colors.lightPinkishRed,
                      color: colors.darkMaroon,
                      cursor: "pointer",
                    }}
                    icon={<CloseCircleFilled />}
                    onClick={(e) =>
                      handleConfirmStatus(e, record, "isRejected")
                    }
                  >
                    {t("CommonUtils.reject")}
                  </Tag>
                </Flex>
              ) : (
                <>-</>
              )}
            </>
          );
        },
      },
      {
        title: t("Modules.ServiceProviderAuth.verificationRemarks"),
        dataIndex: "verification_remark",
        key: "verification_remark",
        render: (verification_remark) => (
          <div
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "150px",
              overflow: "hidden",
            }}
          >
            {verification_remark || "-"}
          </div>
        ),
      },
      {
        title: t("Modules.Provider.dateofJoining"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("Modules.ServiceProviderAuth.details"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleConfirmStatus = (e, row, status) => {
    setStatus(status);
    setOpenAcceptRejectStatusDialog(true);
    dispatch(
      Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
        selectedCustomerId: "",
      })
    );
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCustomerId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
          selectedCustomerId,
        })
      );
    }
  };

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
        selectedCustomerId: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.LOGIN_AUTH_DETAILS));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCustomerId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_CUSTOMER_ID, {
          selectedCustomerId,
        })
      );
    }
  };

  return (
    <div className="servicesListContainer">
      <Flex vertical gap="2rem">
        <Flex align="center" justify="space-between">
          <Title level={2}>{t("Modules.ServiceProviderAuth.doctorAuth")}</Title>
          <Input
            size="large"
            suffix={<SearchOutlined />}
            placeholder={t("CommonUtils.searchByName")}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ width: "370px" }}
          />
        </Flex>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={customers?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
      <AcceptRejectStatusDialog
        status={status}
        open={openAcceptRejectStatusDialog}
        onClose={() => {
          setOpenAcceptRejectStatusDialog(false);
          handleGetTableData();
        }}
      />
    </div>
  );
};

export default LoginAuth;
